/**
 * useOverviewData — single entry point for OverviewV2 data.
 *
 * Composes four backend reads + the pure `aggregateOverview` into
 * one hook the cards consume via their parent:
 *
 *   1. GET /api/v1/analysis/{threadId}/results
 *      (the canonical post-aggregation snapshot used by the
 *       legacy ProjectOverview + AthenaResults — already returns
 *       the cross-step record arrays the cards need)
 *   2. GET /workflow/{threadId}/status
 *      (per-agent statuses for the StatusBanner step pills and
 *       the Iso21434Card status badges)
 *   3. GET /api/v1/session/{sessionId}/unr155-checklist
 *      (only after threats are approved; defers the round trip
 *       until the data is meaningful)
 *   4. GET /api/v1/vv-recommendations/{threadId}
 *      (only after concept is approved; defers similarly)
 *
 * The aggregation is `useMemo`d on the four raw inputs — when none
 * change between polling ticks, cards see reference-stable slices.
 *
 * No polling on this surface (yet). Cards rely on the user opening
 * the tab to trigger the fetch; a manual `refetch()` is exposed for
 * a future explicit-refresh button if the dashboard grows one.
 */

import { useCallback, useMemo } from 'react';
import { useStructuredFetch } from '../../athena/hooks/useStructuredFetch';
import { getAnalysisResults } from '../../../api/analysis';
import { getWorkflowStatus } from '../../../api/workflow';
import { getUNR155Checklist } from '../../../api/unr155';
import { getVVRecommendations } from '../../../api/vvRecommendations';
import { aggregateOverview } from '../aggregate/aggregateOverview';
import type { AggregatedOverviewData } from '../aggregate/types';

export interface UseOverviewDataInput {
  threadId?: string | null;
  sessionId?: string | null;
  analysisMode?: 'individual' | 'hierarchical' | null;
}

export interface UseOverviewDataResult {
  data: AggregatedOverviewData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useOverviewData({
  threadId,
  sessionId,
  analysisMode,
}: UseOverviewDataInput): UseOverviewDataResult {
  // ── Analysis results ─────────────────────────────────────────
  const analysisFetcher = useCallback(
    (signal: AbortSignal) => {
      if (!threadId) return Promise.resolve(null);
      // getAnalysisResults takes only the id (the client ignores AbortSignal
      // here); `signal` stays in the fetcher signature for the shared hook API.
      void signal;
      return getAnalysisResults(threadId);
    },
    [threadId],
  );
  const analysis = useStructuredFetch<any, any>({
    fetcher: analysisFetcher,
    enabled: Boolean(threadId),
  });

  // ── Workflow status ──────────────────────────────────────────
  const workflowFetcher = useCallback(
    (signal: AbortSignal) => {
      if (!threadId) return Promise.resolve(null);
      return getWorkflowStatus(threadId, { signal });
    },
    [threadId],
  );
  const workflow = useStructuredFetch<any, any>({
    fetcher: workflowFetcher,
    enabled: Boolean(threadId),
  });

  // ── UN R155 checklist (only when threats step is approved) ───
  // We can't tell that yet at the input level; instead we look at
  // the workflow snapshot once it lands. Cheaper than gating on a
  // selector — the conditional just toggles after first hydrate.
  const threatsApproved = Boolean(
    workflow.data?.agent_progress?.threat_analysis_agent?.review_status === 'approved' ||
      workflow.data?.agent_details?.threat_analysis_agent?.review_status === 'approved',
  );
  const unr155Fetcher = useCallback(
    (_signal: AbortSignal) => {
      if (!sessionId) return Promise.resolve(null);
      return getUNR155Checklist(sessionId);
    },
    [sessionId],
  );
  const unr155 = useStructuredFetch<any, any>({
    fetcher: unr155Fetcher,
    enabled: Boolean(sessionId) && threatsApproved,
  });

  // ── V&V recommendations (only when concept step is approved) ─
  const conceptApproved = Boolean(
    workflow.data?.agent_progress?.cybersecurity_concept_agent?.review_status === 'approved' ||
      workflow.data?.agent_details?.cybersecurity_concept_agent?.review_status === 'approved',
  );
  const vvFetcher = useCallback(
    (_signal: AbortSignal) => {
      if (!threadId) return Promise.resolve(null);
      return getVVRecommendations(threadId);
    },
    [threadId],
  );
  const vv = useStructuredFetch<any, any>({
    fetcher: vvFetcher,
    enabled: Boolean(threadId) && conceptApproved,
  });

  // ── Aggregate ────────────────────────────────────────────────
  const aggregated = useMemo<AggregatedOverviewData | null>(() => {
    if (!threadId) return null;
    return aggregateOverview({
      analysis: analysis.data,
      workflow: workflow.data,
      unr155: unr155.data,
      vvRecommendations: vv.data,
      analysisMode: analysisMode ?? null,
    });
  }, [threadId, analysis.data, workflow.data, unr155.data, vv.data, analysisMode]);

  const loading = analysis.loading || workflow.loading || unr155.loading || vv.loading;
  const error = analysis.error || workflow.error || null;

  const refetch = useCallback(() => {
    analysis.refetch();
    workflow.refetch();
    unr155.refetch();
    vv.refetch();
  }, [analysis, workflow, unr155, vv]);

  return { data: aggregated, loading, error, refetch };
}
